import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { Plus, MoreVertical } from "lucide-react";
import { EmptyWatchlist } from "./components/EmptyWatchlist";
import { ConfirmationModal } from "./components/ConfirmationModal";
import { RenameModal } from "./components/RenameModal";
import CreateWatchlistModal from "./components/CreateWatchlistModal";
import axios from "axios";
import WebsocketWrapperContext from "../../context/WebsocketWrapperContext";
import symbolSearchService from "./components/SymbolSearchService";
import SearchResults from "./components/SearchResults";
import InstrumentModal from "./components/InstrumentModal";
import { useBuySellModalContext } from "../../context/BuySellModalContext";
import OrdersBuySellModal from "../../screens/OrdersPage/components/OrdersBuySellModal";
import CustomToastContent from "../CustomToastContent";
import { toast } from "react-toastify";
import { getCookie } from "../../utils/cookieUtils";
// Custom hook for debounced search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const WatchlistContainer = ({ symbols, isInChartsTab = false }) => {
  const { subscribeToSymbols, symbolData } = useContext(
    WebsocketWrapperContext
  );
  const { setBuySellModalData } = useBuySellModalContext();
  const [watchlistData, setWatchlistData] = useState({});
  const [activeWatchlistId, setActiveWatchlistId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [newWatchlistName, setNewWatchlistName] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showRename, setShowRename] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscribedSymbols, setSubscribedSymbols] = useState(new Set());
  const [isCreatingWatchlist, setIsCreatingWatchlist] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isServiceInitialized, setIsServiceInitialized] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);

  const [isBuySellModalOpen, setIsBuySellModalOpen] = useState(false);
  const [watchlistSearchFocused, setWatchlistSearchFocused] = useState(false);

  // Debounce search text to reduce API calls
  const debouncedSearchText = useDebounce(searchText, 300);

  const API_URL =
    "https://lhijbdwvj4.execute-api.ap-south-1.amazonaws.com/default/getUpdatePositionWatchlist";
  const USER_ID = getCookie("client_id");
  const ACCESS_TOKEN = getCookie("access_token");

  // Initialize symbol search service with better error handling
  useEffect(() => {
    const initializeService = async () => {
      try {
        setServiceError(null);
        // console.log("Initializing symbol search service...");

        const success = await symbolSearchService.initialize();
        if (success) {
          setIsServiceInitialized(true);
          // console.log("Symbol search service initialized successfully");

          // Log cache status for debugging
          const cacheStatus = symbolSearchService.getCacheStatus();
          // console.log("Cache status:", cacheStatus);
        } else {
          throw new Error("Service initialization returned false");
        }
      } catch (error) {
        console.error("Failed to initialize symbol search service:", error);
        setServiceError(error.message);
        setIsServiceInitialized(false);
      }
    };

    initializeService();
  }, []);

  // Optimized search with Web Worker simulation using setTimeout
  const performSearch = useCallback(
    async (searchQuery) => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      if (!isServiceInitialized) {
        // console.log("Service not initialized yet");
        return;
      }

      setIsSearching(true);

      // Use setTimeout to make search non-blocking
      setTimeout(() => {
        try {
          // console.log(`Searching for: "${searchQuery}"`);
          const results = symbolSearchService.searchSymbols(searchQuery);
          // console.log(`Found ${results.length} results`);

          const limitedResults = results.slice(0, 50); //Limiting to 50 for performance for now
          setSearchResults(limitedResults);
        } catch (error) {
          console.error("Search error:", error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      }, 0);
    },
    [isServiceInitialized]
  );

  // debounced search
  useEffect(() => {
    performSearch(debouncedSearchText);
  }, [debouncedSearchText, performSearch]);

  // immediate search input
  const handleSearchInput = useCallback((text) => {
    setSearchText(text);
    if (!text.trim()) {
      setSearchResults([]);
      setIsSearching(false);
    } else {
      setIsSearching(true);
    }
  }, []);

  // Add symbol to watchlist - Fixed API integration
  const addSymbolToWatchlist = useCallback(
    async (symbol) => {
      // console.log("Adding symbol to watchlist", symbol);
      // console.log("activeWatchlistId:", activeWatchlistId);
      // console.log("watchlistData:", watchlistData);
      if (!activeWatchlistId) {
        // console.log("No active watchlist ID, returning early");
        return;
      }

      try {
        // Check if symbol already exists
        const existingInstruments =
          watchlistData[activeWatchlistId]?.instruments || [];
        const symbolExists = existingInstruments.some(
          (inst) => inst.code === symbol.code
        );
        if (symbolExists) {
          alert("Symbol already exists in watchlist");
          return;
        }

        // Prepare API request
        const requestBody = {
          user_id: USER_ID,
          access_token: ACCESS_TOKEN,
          operation: "add",
          page: activeWatchlistId,
          trading_symbol: symbol.trading_symbol,
          exchange: symbol.exch,
          code: symbol.code,
          formatted_ins_name: symbol.formatted_ins_name,
          expiry: symbol.expiry || "null",
          name: symbol.symbol_name,
          lot_size: symbol.lot_size || "1",
          tick_size: symbol.tick_size || "0.01",
          strike_price: symbol.strike_price || "null",
          segment_type: symbol.segment_type,
        };

        // console.log("Adding symbol to watchlist:", requestBody);

        // Make API call
        const response = await axios.post(API_URL, requestBody, {
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (response.data.success) {
          // Immediately update local state to reflect the change
          setWatchlistData(prevData => {
            const updatedData = { ...prevData };
            if (!updatedData[activeWatchlistId]) {
              updatedData[activeWatchlistId] = { instruments: [] };
            }
            if (!updatedData[activeWatchlistId].instruments) {
              updatedData[activeWatchlistId].instruments = [];
            }
            
            // Add the new instrument to the local state
            const newInstrument = {
              trading_symbol: symbol.trading_symbol,
              code: symbol.code,
              formatted_ins_name: symbol.formatted_ins_name,
              name: symbol.symbol_name,
              type: symbol.segment_type,
              exchange: symbol.exch,
              expiry: symbol.expiry || "null",
              lot_size: symbol.lot_size || "1",
              tick_size: symbol.tick_size || "0.01",
              strike_price: symbol.strike_price || "null",
              segment_type: symbol.segment_type,
            };
            
            updatedData[activeWatchlistId].instruments.push(newInstrument);
            return updatedData;
          });

          // Refresh watchlist data from server in background
          getWatchlistData();

          // Subscribe to new symbol
          const exchange = getExchangeFromSegment(symbol.segment_type);
          const symbolKey = `${exchange}_${symbol.code}`;
          if (!subscribedSymbols.has(symbolKey)) {
            subscribeToSymbols([symbol.code], exchange);
            setSubscribedSymbols((prev) => new Set([...prev, symbolKey]));
          }
          setWatchlistSearchFocused(true);
          toast(`${symbol.trading_symbol} added to watchlist: ${watchlistData[activeWatchlistId]?.name}`, {
            position: "bottom-center",
            autoClose: 1000,
            hideProgressBar: true,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "dark",
            className: "custom-snackbar",
            bodyClassName: "custom-snackbar-body",
          });
        } else {
          // console.log("Failed to add symbol", response.data.errorMessage);
          throw new Error(response.data.errorMessage || "Failed to add symbol");
        }
      } catch (error) {
        console.error("Error adding symbol to watchlist:", error);
        alert(
          "Failed to add symbol: " +
            (error.response?.data?.errorMessage || error.message)
        );
      }
    },
    [activeWatchlistId, watchlistData, subscribedSymbols, subscribeToSymbols]
  );

  // Map segment types to exchanges
  const getExchangeFromSegment = (segmentType) => {
    const segmentToExchangeMap = {
      nse_cm: "NSE",
      nse_idx: "NSE",
      nse_fo: "NFO",
      bse_cm: "BSE",
      bse_idx: "BSE",
      bse_fo: "BSE",
      mcx_fo: "MCX",
      mcx_idx: "MCX",
    };
    return segmentToExchangeMap[segmentType] || "NSE";
  };
  const getSegmentCategory = (segmentType) => {
    const segmentMap = {
      nse_idx: "index",
      bse_idx: "index",
      mcx_idx: "index",
      // Add other index segments if needed
    };
    return segmentMap[segmentType] || "other";
  };
  // Subscribe to all symbols from all watchlists
  const subscribeToAllWatchlistSymbols = () => {
    if (!subscribeToSymbols) return;

    // Get all unique instruments from all watchlists
    const allInstruments = [];
    Object.keys(watchlistData).forEach((watchlistId) => {
      if (
        watchlistId !== "last_updated" &&
        watchlistData[watchlistId].instruments
      ) {
        allInstruments.push(...watchlistData[watchlistId].instruments);
      }
    });

    if (allInstruments.length === 0) return;

    // Group instruments by exchange
    const instrumentsByExchange = {};

    allInstruments.forEach((instrument) => {
      const exchange = getExchangeFromSegment(instrument.segment_type);
      if (!instrumentsByExchange[exchange]) {
        instrumentsByExchange[exchange] = [];
      }

      // Only add if not already subscribed
      if (!subscribedSymbols.has(`${exchange}_${instrument.code}`)) {
        instrumentsByExchange[exchange].push(instrument.code);
      }
    });

    // Subscribe to symbols for each exchange
    Object.entries(instrumentsByExchange).forEach(([exchange, codes]) => {
      if (codes.length > 0) {

        subscribeToSymbols(codes, exchange);

        // Mark these symbols as subscribed
        codes.forEach((code) => {
          setSubscribedSymbols(
            (prev) => new Set([...prev, `${exchange}_${code}`])
          );
        });
      }
    });
  };
  const getWatchlistData = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        API_URL,
        {
          user_id: USER_ID,
          access_token: ACCESS_TOKEN,
          operation: "get",
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.data.statusCode === 200) {
        const newData = response.data.watchlist;
        setWatchlistData(newData);

        // Preserve current active watchlist if it still exists
        const watchlistIds = Object.keys(newData).filter(
          (id) => id !== "last_updated"
        );

        // Check if current active watchlist still exists
        const currentStillExists = watchlistIds.includes(
          activeWatchlistId?.toString()
        );

        if (!currentStillExists || !activeWatchlistId) {
          // If current active was deleted or doesn't exist, find first non-empty watchlist
          const firstNonEmptyWatchlist = watchlistIds.find(
            (id) => newData[id].name && newData[id].name.trim()
          );
          setActiveWatchlistId(firstNonEmptyWatchlist || watchlistIds[0]);
        }


      }
    } catch (error) {
      console.error("Error fetching watchlist:", error);
    } finally {
      setLoading(false);
    }
  };

  const getAvailableWatchlists = () => {
    return Object.keys(watchlistData)
      .filter((id) => id !== "last_updated")
      .filter((id) => watchlistData[id].name && watchlistData[id].name.trim())
      .map((id) => ({
        id,
        name: watchlistData[id].name,
      }));
  };

  const getNextAvailableSlot = () => {
    const usedSlots = Object.keys(watchlistData).filter(
      (id) => id !== "last_updated"
    );
    for (let i = 1; i <= 7; i++) {
      if (
        !usedSlots.includes(i.toString()) ||
        !watchlistData[i.toString()]?.name
      ) {
        return i.toString();
      }
    }
    return null;
  };

  // Fixed create watchlist with API integration
  const handleCreateWatchlist = async () => {
    if (!newWatchlistName.trim()) return;

    const nextSlot = getNextAvailableSlot();
    if (!nextSlot) {
      alert("Maximum watchlists limit reached!");
      return;
    }

    try {
      setIsCreatingWatchlist(true);

      const requestBody = {
        user_id: USER_ID,
        access_token: ACCESS_TOKEN,
        operation: "create",
        page: nextSlot,
        list_name: newWatchlistName.trim(),
      };

      // console.log("Creating watchlist:", requestBody);

      const response = await axios.post(API_URL, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success || response.data.statusCode === 200) {
        // Refresh watchlist data from server
        await getWatchlistData();
        setActiveWatchlistId(nextSlot);
        setNewWatchlistName("");
        setShowPopup(false);
        toast("Watchlist created successfully!", {
          position: "bottom-center",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          className: "custom-snackbar",
          bodyClassName: "custom-snackbar-body",
        });
      } else {
        throw new Error(
          response.data.errorMessage || "Failed to create watchlist"
        );
      }
    } catch (error) {
      toast.error(
        <CustomToastContent
          heading={"Failed to create watchlist"}
          message={`${error.response?.data?.errorMessage || error.message}`}
        />
      );
    } finally {
      setIsCreatingWatchlist(false);
    }
  };

  // Clear watchlist - Fixed with API integration
  const handleClearWatchlist = () => {
    setConfirmAction({
      type: "clear",
      title: "Clear Watchlist",
      message: `Are you sure you want to clear all instruments from "${watchlistData[activeWatchlistId]?.name}"?`,
      confirmText: "Yes, Clear",
    });
    setShowConfirm(true);
    setShowDropdown(false);
  };

  // Delete watchlist - Fixed with API integration
  const handleDeleteWatchlist = () => {
    const availableWatchlists = getAvailableWatchlists();
    if (availableWatchlists.length <= 1) {
      alert("Cannot delete the last watchlist!");
      setShowDropdown(false);
      return;
    }

    setConfirmAction({
      type: "delete",
      title: "Delete Watchlist",
      message: `Are you sure you want to delete the watchlist "${watchlistData[activeWatchlistId]?.name}"?`,
      confirmText: "Yes, Delete",
    });
    setShowConfirm(true);
    setShowDropdown(false);
  };

  // Rename watchlist
  const handleRenameWatchlist = () => {
    setShowRename(true);
    setShowDropdown(false);
  };

  // Confirm clear action - Fixed with API integration
  const confirmClear = async () => {
    try {
      const requestBody = {
        user_id: USER_ID,
        access_token: ACCESS_TOKEN,
        operation: "clear_list",
        page: activeWatchlistId,
      };

      // console.log("Clearing watchlist:", requestBody);

      const response = await axios.post(API_URL, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success || response.data.statusCode === 200) {
        // Refresh watchlist data from server
        await getWatchlistData();
        setShowConfirm(false);
        toast("Watchlist cleared successfully!", {
          position: "bottom-center",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          className: "custom-snackbar",
          bodyClassName: "custom-snackbar-body",
        });
      } else {
        throw new Error(
          response.data.errorMessage || "Failed to clear watchlist"
        );
      }
    } catch (error) {
      console.error("Error clearing watchlist:", error);
      alert(
        "Failed to clear watchlist: " +
          (error.response?.data?.errorMessage || error.message)
      );
    }
  };

  // Confirm delete action - Fixed with API integration
  const confirmDelete = async () => {
    try {
      const requestBody = {
        user_id: USER_ID,
        access_token: ACCESS_TOKEN,
        operation: "delete_list",
        page: activeWatchlistId,
      };

      // console.log("Deleting watchlist:", requestBody);

      const response = await axios.post(API_URL, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success || response.data.statusCode === 200) {
        // Refresh watchlist data from server
        await getWatchlistData();

        // Set active watchlist to first available watchlist
        const availableWatchlists = getAvailableWatchlists();
        const remainingWatchlists = availableWatchlists.filter(
          (w) => w.id !== activeWatchlistId
        );
        if (remainingWatchlists.length > 0) {
          setActiveWatchlistId(remainingWatchlists[0].id);
        }

        setShowConfirm(false);
        toast("Watchlist deleted successfully!", {
          position: "bottom-center",
          autoClose: 1000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
          className: "custom-snackbar",
          bodyClassName: "custom-snackbar-body",
        });
      } else {
        throw new Error(
          response.data.errorMessage || "Failed to delete watchlist"
        );
      }
    } catch (error) {
      toast.error(
        <CustomToastContent
          heading={"Failed to delete watchlist"}
          message={`${error.response?.data?.errorMessage || error.message}`}
        />
      );
    }
  };

  // Confirm rename action - Fixed with API integration
  const confirmRename = async (newName) => {
    try {
      const requestBody = {
        user_id: USER_ID,
        access_token: ACCESS_TOKEN,
        operation: "rename_list",
        page: activeWatchlistId,
        list_name: newName.trim(),
      };

      // console.log("Renaming watchlist:", requestBody);

      const response = await axios.post(API_URL, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success || response.data.statusCode === 200) {
        // Refresh watchlist data from server
        await getWatchlistData();
        setShowRename(false);
        toast.success(
          <CustomToastContent
            heading={"Watchlist renamed successfully"}
            message={`${watchlistData[activeWatchlistId]?.name} watchlist renamed to ${newName}`}
          />
        );
      } else {
        throw new Error(
          response.data.errorMessage || "Failed to rename watchlist"
        );
      }
    } catch (error) {
      toast.error(
        <CustomToastContent
          heading={"Failed to rename watchlist"}
          message={`${error.response?.data?.errorMessage || error.message}`}
        />
      );
    }
  };

  // Handle confirmation modal actions
  const handleConfirmAction = () => {
    if (confirmAction?.type === "clear") {
      confirmClear();
    } else if (confirmAction?.type === "delete") {
      confirmDelete();
    }
  };

  // Delete instrument from watchlist - Fixed with API integration
  const deleteInstrumentFromWatchlist = async (instrument) => {
    try {
      const requestBody = {
        user_id: USER_ID,
        access_token: ACCESS_TOKEN,
        operation: "delete",
        page: activeWatchlistId,
        trading_symbol: instrument.symbol,
      };

      // console.log("Deleting instrument:", requestBody);

      const response = await axios.post(API_URL, requestBody, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success || response.data.statusCode === 200) {
        // Refresh watchlist data from server
        await getWatchlistData();
        // Close modal after successful deletion
        closeModal();
        // console.log("Instrument deleted successfully");
      } else {
        throw new Error(
          response.data.errorMessage || "Failed to delete instrument"
        );
      }
    } catch (error) {
      console.error("Error deleting instrument:", error);
      alert(
        "Failed to delete instrument: " +
          (error.response?.data?.errorMessage || error.message)
      );
    }
  };

  // Calculate change percentage and format
  const calculateChange = (ltp, prevClose) => {
    if (!ltp || !prevClose || prevClose === 0) return "0.00 (0.0%)";

    const change = ltp - prevClose;
    const changePercent = (change / prevClose) * 100;
    const sign = change >= 0 ? "+" : "";

    return `${sign}${change.toFixed(2)} (${sign}${changePercent.toFixed(1)}%)`;
  };

  // Format instrument display with live data - memoized for performance
  const formatInstrument = useCallback(
    (instrument) => {
      const getInstrumentType = (segment) => {
        switch (segment) {
          case "nse_cm":
            return "NSE / Stocks";
          case "nse_idx":
            return "NSE / Index";
          case "nse_fo":
            return "NFO / F&O";
          case "bse_cm":
            return "BSE / Stocks";
          case "bse_idx":
            return "BSE / Index";
          case "mcx_fo":
            return "MCX / F&O";
          case "bse_fo":
            return "BSE / F&O";
          case "mcx_idx":
            return "MCX / Index";
          default:
            return `${instrument.exchange} / ${segment}`;
        }
      };

      const code = instrument.code;
      const exchange = getExchangeFromSegment(instrument.segment_type);
      const liveData = symbolData[code] || {};

      // Get live price data
      const ltp = liveData.ltp || 0;
      const prevClose = liveData.prev_close || 0;
      const change = calculateChange(ltp, prevClose);
      const changeValue = ltp - prevClose;

      return {
        name: instrument?.formatted_ins_name,
        type: getInstrumentType(instrument?.segment_type),
        price: ltp ? ltp : "0.00",
        change: change,
        changeValue: changeValue,
        symbol: instrument.trading_symbol,
        exchange: exchange,
        code: code,
        originalInstrument: instrument, // Keep reference for deletion
      };
    },
    [symbolData]
  );

  // Memoize current instruments for performance
  const currentInstruments = useMemo(() => {
    if (!activeWatchlistId || !watchlistData[activeWatchlistId]) return [];
    return watchlistData[activeWatchlistId].instruments.map(formatInstrument);
  }, [activeWatchlistId, watchlistData, formatInstrument]);

  const availableWatchlists = getAvailableWatchlists();
  const currentWatchlistName = watchlistData[activeWatchlistId]?.name || "";

  // Subscribe to symbols when watchlist data is loaded
  useEffect(() => {
    if (Object.keys(watchlistData).length > 0) {
      subscribeToAllWatchlistSymbols();
    }
  }, [watchlistData, subscribeToSymbols]);

  useEffect(() => {
    getWatchlistData();
  }, []);

  const handleInstrumentClick = (instrument) => {
    setSelectedInstrument(instrument);
    setIsModalOpen(true);
  };

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedInstrument(null);
  }, []);

  const prepareTradeDataForInstrument = (instrument, side) => {
    const ltpPrice = symbolData[instrument.code]?.ltp || 0;

    return {
      side: side,
      instrumentName: instrument.name,
      lotSize: 1,
      lot_size: instrument.originalInstrument?.lot_size || 1,
      productType: "Intraday",
      code: instrument.code,
      price: ltpPrice,
      trigger_price: 0,
      buy_sell: side === "Buy" ? "B" : "S",
      tradingSymbol: instrument.symbol,
      quantity: instrument.originalInstrument?.lot_size || 1,
      exchange: instrument.exchange,
      orderStatus: "New",
      scrip: instrument.symbol,
      orderType: "MARKET",
    };
  };

  const handleBuyClick = (instrument) => {
    const tradeData = prepareTradeDataForInstrument(instrument, "Buy");
    setBuySellModalData([tradeData]);
    setIsBuySellModalOpen(true);
    closeModal(); // instrument modal
  };

  const handleSellClick = (instrument) => {
    const tradeData = prepareTradeDataForInstrument(instrument, "Sell");
    setBuySellModalData([tradeData]);
    setIsBuySellModalOpen(true);
    closeModal();
  };

  // Add handler to close buy/sell modal
  const handleBuySellModalClose = () => {
    setIsBuySellModalOpen(false);
  };

  const fetchOrders = () => {
    // console.log("Fetching updated orders");
  };
  return (
    <div className={`flex flex-col ${isInChartsTab ? 'h-full w-full' : ''}`}>
      {/* Search Bar */}
      <div className={`flex flex-col gap-3 ${isInChartsTab ? '' : 'bg-p400'} border border-n100 rounded-md w-full  mt-3 relative flex-shrink-0`}>
        <div className="relative">
          <input
            type="text"
            placeholder={
              !isServiceInitialized
                ? "Loading search data..."
                : serviceError
                  ? "Search unavailable"
                  : "Search instruments"
            }
            className="w-full h-[46px] rounded-[4px] border-none pl-8 pr-10 text-xs font-medium text-n600 focus:outline-none bg-p400"
            value={searchText}
            onChange={(e) => handleSearchInput(e.target.value)}
            disabled={!isServiceInitialized || serviceError}
            spellCheck={false}
            onFocus={() => setWatchlistSearchFocused(true)}
            onBlur={() => setWatchlistSearchFocused(false)}
          />
          <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
            {isSearching ? (
              <div className="animate-spin h-4 w-4 border-2 border-gray-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <circle
                  cx="11"
                  cy="11"
                  r="8"
                  stroke="#6B7280"
                  strokeWidth="2"
                />
                <path d="m21 21-4.35-4.35" stroke="#6B7280" strokeWidth="2" />
              </svg>
            )}
          </div>

          {/* Clear text button */}
          {searchText && (
            <button
              type="button"
              className="absolute right-5 top-1/2 transform -translate-y-1/2 text-n400 hover:text-n600 focus:outline-none"
              aria-label="Clear search"
              onClick={() => handleSearchInput("")}
              tabIndex={0}>
              <svg
                width="24"
                height="24"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7 7l6 6M13 7l-6 6"
                  stroke="#6B7280"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}

          {/* Service status indicator */}
          {!isServiceInitialized && !serviceError && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <div className="h-2 w-2 bg-yellow-500 rounded-full animate-pulse"></div>
            </div>
          )}

          {serviceError && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              <div className="h-2 w-2 bg-red-500 rounded-full"></div>
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchText &&
          isServiceInitialized &&
          !serviceError &&
          watchlistSearchFocused && (
            <>
              <div className="search-results-container">
                <SearchResults
                  results={searchResults}
                  onSelect={addSymbolToWatchlist}
                  isLoading={isSearching}
                  alreadyAdded={
                    watchlistData[activeWatchlistId]?.instruments?.map(
                      (ins) => ins.formatted_ins_name
                    ) || []
                  }
                />
              </div>
            </>
          )}

        {/* Service Error Message */}
        {serviceError && (
          <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded">
            Search service error: {serviceError}
          </div>
        )}
      </div>

      {/* Main body */}
      <div className={`relative ${isInChartsTab ? 'flex-1 flex flex-col min-h-0' : ''}`}>
        <div className={`flex flex-col ${isInChartsTab ? 'h-full' : 'h-[350px] max-w-[500px]'} ${isInChartsTab ? '' : 'bg-p400'} border border-n100 rounded-md w-full overflow-hidden mt-3 no-scrollbar`}>
          {/* Main scrollable content area */}
          <div className="flex-1 overflow-y-auto p-4 no-scrollbar min-h-0">
            {currentInstruments.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <EmptyWatchlist watchlistName={currentWatchlistName} />
              </div>
            ) : (
              <div className="space-y-3">
                {currentInstruments.map((instrument, index) => (
                  <div
                    key={index}
                    className="border-b border-n100 pb-3 last:border-b-0 cursor-pointer"
                    onClick={() => handleInstrumentClick(instrument)}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-medium text-p300 text-sm">
                          {instrument.name}
                        </h3>
                        <p className="text-xs text-n500 mt-1 font-medium">
                          {instrument.type}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-p300">
                          ₹ {instrument.price}
                        </p>
                        <p
                          className={`text-xs mt-1 font-medium ${
                            instrument.changeValue >= 0
                              ? "text-p150"
                              : "text-red"
                          }`}>
                          {instrument.change}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Bottom horizontal scrollable watchlist tabs */}
          <div className="border-t border-n200 p-3">
            <div className="flex items-center gap-2">
              {/* Scrollable watchlist names container */}
              <div className="flex-1 overflow-x-auto">
                <div className="flex gap-2 min-w-max">
                  {availableWatchlists.map((watchlist) => (
                    <button
                      key={watchlist.id}
                      onClick={() => setActiveWatchlistId(watchlist.id)}
                      className={`py-1.5 px-2 text-sm rounded whitespace-nowrap transition-colors ${
                        activeWatchlistId === watchlist.id
                          ? "border-b-2 border-p150 font-medium"
                          : "font-normal"
                      }`}>
                      {watchlist.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fixed buttons container */}
              <div className="flex gap-2 items-center flex-shrink-0">
                {/* Add watchlist button */}
                <button
                  onClick={() => {
                    if (availableWatchlists.length >= 7) {
                      toast(`Can create only maximum of 7 watchlists at the moment`, {
                        position: "bottom-center",
                        autoClose: 1000,
                        hideProgressBar: true,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "dark",
                        className: "custom-snackbar",
                        bodyClassName: "custom-snackbar-body",
                      });
                      return;
                    }
                    setShowPopup(true)}
                  }
                  className="p-1 hover:bg-n100 rounded">
                  <Plus size={20} className="text-blue-500" />
                </button>

                {/* Three dots dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="p-1 hover:bg-n100 rounded">
                    <MoreVertical size={20} className="text-p300" />
                  </button>

                  {/* Dropdown Menu */}
                  {showDropdown && (
                    <div className="absolute bottom-full right-0 mb-2 w-44 bg-p400 border border-n200 rounded-lg shadow-lg z-10">
                      <div className="py-1">
                        <button
                          onClick={handleClearWatchlist}
                          className="w-full px-4 py-2 text-left text-sm text-p300 hover:bg-n100 flex items-center gap-2">
                          Clear watchlist
                        </button>
                        <button
                          onClick={handleRenameWatchlist}
                          className="w-full px-4 py-2 text-left text-sm text-p300 hover:bg-n100 flex items-center gap-2">
                          Rename watchlist
                        </button>
                        <button
                          onClick={handleDeleteWatchlist}
                          className="w-full px-4 py-2 text-left text-sm text-p300 hover:bg-n100 flex items-center gap-2">
                          Delete watchlist
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create watchlist popup */}
        {showPopup && (
          <div className="fixed inset-0 bg-[#0000004d] bg-opacity-50 flex items-center justify-center z-50">
            <CreateWatchlistModal
              isOpen={showPopup}
              newWatchlistName={newWatchlistName}
              setNewWatchlistName={setNewWatchlistName}
              onClose={() => setShowPopup(false)}
              onCreate={handleCreateWatchlist}
            />
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirm}
          onConfirm={handleConfirmAction}
          onCancel={() => setShowConfirm(false)}
          title={confirmAction?.title || "Confirmation"}
          message={confirmAction?.message || "Are you sure?"}
          confirmText={confirmAction?.confirmText || "Yes, Proceed"}
        />

        {/* Rename Modal */}
        {showRename && (
          <RenameModal
            onConfirm={confirmRename}
            onCancel={() => setShowRename(false)}
            currentName={currentWatchlistName}
          />
        )}

        <InstrumentModal
          isOpen={isModalOpen}
          onClose={closeModal}
          instrument={selectedInstrument}
          allSymbols={symbols}
          onDelete={deleteInstrumentFromWatchlist}
          isIndex={
            selectedInstrument?.originalInstrument?.segment_type
              ? getSegmentCategory(
                  selectedInstrument.originalInstrument.segment_type
                ) === "index"
              : false
          }
          onBuy={handleBuyClick}
          onSell={handleSellClick}
        />

        {isBuySellModalOpen && (
          <OrdersBuySellModal
            modalOpen={isBuySellModalOpen}
            modalClose={handleBuySellModalClose}
            isOrders={true}
            setIsBuySellModalOpen={setIsBuySellModalOpen}
            fetchOrders={fetchOrders}
          />
        )}

        {/* Backdrop to close dropdown */}
        {showDropdown && (
          <div
            className="fixed inset-0 z-5"
            onClick={() => setShowDropdown(false)}
          />
        )}
      </div>
    </div>
  );
};

export default WatchlistContainer;
